import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import XGSLeafletMap from "ui-components/map/leaflet/XGSLeafletMap";
import type {
  LeafletMarkerModel,
  LeafletMarkerKind,
} from "ui-components/map/leaflet/xgsLeafletMap.types";
import { pickupAssignmentSelector } from "../../../slices/pickup/pickupAssignmentSlice";
import { DriversResponseContentModel } from "../../../app/data/pickup/models";
import { getStopFillColor, TRUCK_MARKER } from "../../../services/map";
import { PickupStatus, StopType } from "../../../app/data/common/route";
import XGSMarkerTooltip from "../../../ui-components/map/marker-tooltip/markerTooltip";
import StopMarkerInfo from "../../../ui-components/map/stop-marker-info/stopMarkerInfo";
import { getCoordinates } from "../../../app/data/map/utils";
import { formatAddress } from "../../../services/common/address";
import { getPickupGroupStatus, getPickupsByIds } from "../../../services/pickups";
import "../../../ui-components/map/map.scss";
import { decodeGooglePolylineToLatLng } from "ui-components/map/leaflet/decodeGooglePolyline";

export interface PickupMapProps {
  driverId?: number | null;
  pickupIds: string[];
  showAllRoutes?: boolean;
  onDriverSelect: (id: number) => void;
}

const US_CENTER: [number, number] = [40.329796, -98.641933];

const PickupMap: React.FC<PickupMapProps> = (props) => {
  const pickupAssignmentState = useSelector(pickupAssignmentSelector);
  const [driver, setDriver] = useState<DriversResponseContentModel | undefined>(undefined);

  const pickup = useMemo(() => {
    const pickups = getPickupsByIds(pickupAssignmentState.pickups, props.pickupIds);

    const pickupStatus = pickups.length ? getPickupGroupStatus(pickups) : null;

    return pickups[0] && {
      ...pickups[0],
      status: pickupStatus,
    };

  }, [props.pickupIds, pickupAssignmentState.pickups]);

  const pickupLocationDefined = !!(pickup && pickup.shipper.address.geolocation);

  useEffect(() => {
    if (props.driverId) {
      const driverObj = pickupAssignmentState.drivers.find(
        (obj) => obj.driverId === props.driverId
      );
      setDriver(driverObj);
    } else {
      setDriver(undefined);
    }
  }, [props.driverId, pickupAssignmentState.drivers]);

  const displayedDrivers = useMemo(() => {
    return driver
      ? [driver]
      : props.showAllRoutes
        ? pickupAssignmentState.drivers
        : [];
  }, [driver, props.showAllRoutes, pickupAssignmentState.drivers]);

  const defaultCenter: [number, number] = useMemo(() => {
    return pickupLocationDefined
      ? [pickup.shipper.address.geolocation.lat, pickup.shipper.address.geolocation.lng]
      : US_CENTER;
  }, [pickupLocationDefined, pickup]);

  const markers: LeafletMarkerModel[] = useMemo(() => {
    if (!pickupLocationDefined) return [];

    return [
      {
        id: `${pickup.pickup.pickupNumber}-${pickup.status}`,
        kind: "pickup" as const,
        lat: pickup.shipper.address.geolocation.lat,
        lng: pickup.shipper.address.geolocation.lng,
        label: "PU",
        fillColor: getStopFillColor(StopType.PICKUP, pickup.status),
      },
    ];
  }, [pickup, pickupLocationDefined]);

  const driverMarkers: LeafletMarkerModel[] = useMemo(() => {
    if (!pickup) return [];

    return displayedDrivers
      .filter((d) => d.driverLocation)
      .map((d) => ({
        id: `driver-${d.driverId}`,
        kind: "driver" as const,
        lat: d.driverLocation.lat,
        lng: d.driverLocation.lng,
        shapeSvgPath: TRUCK_MARKER.PATH,
        shapeColor: TRUCK_MARKER.COLOR,
      }));
  }, [displayedDrivers, pickup]);

  const driverStopMarkers: LeafletMarkerModel[] = useMemo(() => {
    if (!pickup) return [];

    return displayedDrivers.flatMap((d) =>
      d.stops
        .map((stop, index) => {
          const coords = getCoordinates(stop.address);
          if (!coords?.lat || !coords?.lng) return null;
          return {
            id: `driver-${d.driverId}-stop-${index}`,
            kind: (stop.type === "DELIVERY" ? "delivery" : "service") as LeafletMarkerKind,
            lat: coords.lat,
            lng: coords.lng,
            label: stop.type === "DELIVERY" ? `${stop.order}` : "SC",
            fillColor: getStopFillColor(
              stop.type === "DELIVERY" ? StopType.STOP : StopType.SERVICE_CENTER,
              stop.status
            ),
          };
        })
        .filter(Boolean)
    ) as LeafletMarkerModel[];
  }, [displayedDrivers, pickup]);

  const driverPickupMarkers: LeafletMarkerModel[] = useMemo(() => {
    if (!pickup) return [];

    return displayedDrivers.flatMap((d) =>
      (d.pickups ?? [])
        .filter((p) => p.shipper.address.geolocation)
        .map((p) => ({
          id: `driver-${d.driverId}-pickup-${p.pickupNumber}`,
          kind: "pickup" as const,
          lat: p.shipper.address.geolocation.lat,
          lng: p.shipper.address.geolocation.lng,
          label: "PU",
          fillColor: getStopFillColor(StopType.PICKUP, PickupStatus[p.pickupStatus]),
        }))
    );
  }, [displayedDrivers, pickup]);

  const driverPolylines = useMemo(() => {
    if (!pickup) return [];

    return displayedDrivers.map((d) => {

      if (d.polyline) {
        return {
          id: `polyline-driver-${d.driverId}`,
          positions: decodeGooglePolylineToLatLng(d.polyline),  // decode the encoded string
          color: "#23356d",
          weight: 3,
          opacity: 0.8,
          tooltip: `Driver #${d.driverId}`,
        };
      }

      const positions: [number, number][] = [
        ...(d.driverLocation
          ? [[d.driverLocation.lat, d.driverLocation.lng] as [number, number]]
          : []),
        ...d.stops.map((stop) => {
          const coords = getCoordinates(stop.address);
          return [coords.lat, coords.lng] as [number, number];
        }),
        ...(d.pickups ?? [])
          .filter((p) => p.shipper.address.geolocation) 
          .map((p) => [
            p.shipper.address.geolocation.lat,
            p.shipper.address.geolocation.lng,
          ] as [number, number]),
      ];

      return {
        id: `polyline-driver-${d.driverId}`,
        positions,
        color: "#23356d",
        weight: 3,
        opacity: 0.8,
        tooltip: `Driver #${d.driverId}`,
      };
    });
  }, [displayedDrivers, pickup]);

  const allMarkers = useMemo(() => [
    ...markers,
    ...driverMarkers,
    ...driverStopMarkers,
    ...driverPickupMarkers,
  ], [markers, driverMarkers, driverStopMarkers, driverPickupMarkers]);

  const renderPopup = (id: string, kind: LeafletMarkerKind) => {
    // Selected pickup marker
    if (pickup && id === `${pickup.pickup.pickupNumber}-${pickup.status}`) {
      return (
        <XGSMarkerTooltip>
          <StopMarkerInfo
            info={{ address: formatAddress(pickup.shipper.address) }}
          />
        </XGSMarkerTooltip>
      );
    }

    // Driver truck marker: "driver-{driverId}"
    if (id.startsWith("driver-") && !id.includes("-stop-") && !id.includes("-pickup-")) {
      const driverId = parseInt(id.replace("driver-", ""), 10);
      const driverObj = displayedDrivers.find((d) => d.driverId === driverId);
      if (driverObj?.driverLocation) {
        return (
          <XGSMarkerTooltip>
            <StopMarkerInfo
              info={{
                address: driverObj.driverLocation.formattedAddress,
                driver: props.showAllRoutes ? driverObj.driverName : undefined,
              }}
            />
          </XGSMarkerTooltip>
        );
      }
    }

    // Driver stop marker: "driver-{driverId}-stop-{index}"
    if (id.includes("-stop-")) {
      const match = id.match(/^driver-(\d+)-stop-(\d+)$/);
      if (match) {
        const driverId = parseInt(match[1], 10);
        const stopIndex = parseInt(match[2], 10);
        const driverObj = displayedDrivers.find((d) => d.driverId === driverId);
        const stop = driverObj?.stops[stopIndex];
        if (stop) {
          return (
            <XGSMarkerTooltip>
              <StopMarkerInfo
                info={{
                  address: formatAddress(stop.address),
                  driver: props.showAllRoutes ? driverObj.driverName : undefined,
                }}
              />
            </XGSMarkerTooltip>
          );
        }
      }
    }

    // Driver pickup marker: "driver-{driverId}-pickup-{pickupNumber}"
    if (id.includes("-pickup-")) {
      const match = id.match(/^driver-(\d+)-pickup-(.+)$/);
      if (match) {
        const driverId = parseInt(match[1], 10);
        const pickupNumber = match[2];
        const driverObj = displayedDrivers.find((d) => d.driverId === driverId);
        const driverPickup = driverObj?.pickups?.find((p) => p.pickupNumber === pickupNumber);
        if (driverObj && driverPickup) {
          return (
            <XGSMarkerTooltip>
              <StopMarkerInfo
                info={{
                  address: formatAddress(driverPickup.shipper.address),
                  driver: `${driverObj.driverName} - ${driverObj.driverId}`,
                }}
              />
            </XGSMarkerTooltip>
          );
        }
      }
    }

    return null;
  };

  const handleMarkerClick = (id: string, kind: LeafletMarkerKind) => {
    const clickedDriver = displayedDrivers.find(
      (d) => `driver-${d.driverId}` === id
    );
    if (clickedDriver) {
      props.onDriverSelect(clickedDriver.driverId);
    }
  };

  return (
    <div>
      <div className="xgs-map__container">
        <XGSLeafletMap
          key={`map-${props.driverId ?? 'no-driver'}`}
          markers={allMarkers}
          polylines={driverPolylines}
          renderPopup={renderPopup}
          onMarkerClick={handleMarkerClick}
          defaultCenter={defaultCenter}
          defaultZoom={pickupLocationDefined ? 6 : 4}
        />
      </div>
    </div>
  );
};

export default PickupMap;